import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, 
        { email, password },
        { withCredentials: true }
      );
      setUser(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      const message = formatApiError(error.response?.data?.detail);
      return { success: false, error: message };
    }
  };

  const register = async (email, password, name, companyName = null) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, 
        { email, password, name, company_name: companyName },
        { withCredentials: true }
      );
      // If pending approval, don't set user
      if (response.data.pending_approval) {
        return { success: true, pending: true, data: response.data };
      }
      setUser(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      const message = formatApiError(error.response?.data?.detail);
      return { success: false, error: message };
    }
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      console.error('Refresh user error:', error);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/session`, 
        { session_id: sessionId },
        { withCredentials: true }
      );
      setUser(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      const message = formatApiError(error.response?.data?.detail);
      return { success: false, error: message };
    }
  };

  const updateLanguage = async (language) => {
    try {
      await axios.put(`${API_URL}/api/auth/language?language=${language}`, {}, { withCredentials: true });
      setUser(prev => prev ? { ...prev, language } : null);
    } catch (error) {
      console.error('Language update error:', error);
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    googleLogin,
    processGoogleSession,
    updateLanguage,
    checkAuth,
    refreshUser,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Helper function to format API errors
function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default AuthContext;
